import { useMemo, useState } from 'react';
import { useGT } from 'gt-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import { ArrowsOutIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatMonthShortLabel } from '@/utils/dateUtils';

// One month of sales at a (product, warehouse) — shape matches the
// `NeedSalesPoint` returned by the procurement RPCs (monthStart = YYYY-MM-01).
export interface SalesHistoryPoint {
  monthStart: string;
  quantity: number;
  // Distinct sales orders behind that month's quantity. Units alone can't tell
  // one bulk order from thirty repeat customers, and only the second is a
  // pattern worth stocking against.
  orderCount: number;
  // The share of `quantity` a sales-history copy/transfer rule attributed here
  // from another coordinate; the rest is what this SKU×warehouse sold itself.
  // Optional because older callers (and every read with no rules) carry only
  // the total; absent is treated as "all native".
  attributedQuantity?: number;
}

export interface OnHandOverlayPoint {
  monthStart: string;
  onHand: number;
}

// One forward month of the demand-forecast curve. Shape matches the
// `ForecastPoint` returned by the procurement RPCs (monthStart = YYYY-MM-01).
// `lower`/`upper` are an optional confidence band (currently always null).
export interface ForecastPoint {
  monthStart: string;
  quantity: number;
  lower?: number | null;
  upper?: number | null;
}

interface SalesHistoryChartProps {
  salesHistory: SalesHistoryPoint[];
  // Optional on-hand line overlay (end-of-month). Plots on a secondary Y axis
  // so the bar/line scales don't compete.
  onHandHistory?: OnHandOverlayPoint[];
  // Optional forward demand-forecast curve. When present, the x-axis extends
  // past "now" and the forecast renders as faded bars alongside the history
  // bars, with a boundary marker at the present month.
  forecast?: ForecastPoint[];
  // Optional heading shown above the chart inside the framing card.
  title?: string;
  // Compact (32rem) for inline panels, larger for page-level placements.
  height?: number;
  // Number of trailing months to render continuously (defaults to 12).
  // Months without sales render as 0-bars so gaps are visible.
  monthsBack?: number;
}

// One row of the chart's combined timeline: trailing history (`quantity`, solid
// bars) plus forward forecast months (`forecast`, faded bars). Only the current
// month carries both (partial actual + its forecast).
interface TimelineRow {
  monthStart: string;
  quantity?: number;
  orderCount?: number;
  forecast?: number;
  // Native/attributed split of `quantity`; see SalesHistoryPoint.
  nativeQuantity?: number;
  attributedQuantity?: number;
}

// Previous YYYY-MM-01 month key (UTC) for a YYYY-MM-01 key.
function prevMonthKey(key: string): string {
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}-01`;
}

// Build the continuous month timeline: the trailing window
// [today − (monthsBack − 1) … today] merged with `salesHistory` (missing months
// = 0-bars), then the forecast overlaid on top as a parallel series. The
// forecast typically starts at the current month (the engine excludes the
// partial current month from *history* but still forecasts it), so the current
// month can carry BOTH a partial actual bar and a forecast bar. Forecast months
// beyond the trailing window are appended as forecast-only rows. `boundaryKey`
// is the last complete actual month before the forecast begins — the
// actuals│forecast divider — or null when there is no forecast.
function buildTimeline(
  salesHistory: SalesHistoryPoint[],
  monthsBack: number,
  forecast: ForecastPoint[],
): { rows: TimelineRow[]; boundaryKey: string | null } {
  const histByKey = new Map<string, number>();
  const ordersByKey = new Map<string, number>();
  const attributedByKey = new Map<string, number>();
  for (const p of salesHistory) {
    histByKey.set(p.monthStart, p.quantity);
    ordersByKey.set(p.monthStart, p.orderCount);
    attributedByKey.set(p.monthStart, p.attributedQuantity ?? 0);
  }
  const forecastByKey = new Map<string, number>();
  for (const p of forecast) forecastByKey.set(p.monthStart, p.quantity);

  const now = new Date();
  const startYear = now.getUTCFullYear();
  const startMonth = now.getUTCMonth(); // 0-indexed

  const historyKeys: string[] = [];
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(startYear, startMonth - i, 1));
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    historyKeys.push(`${yyyy}-${mm}-01`);
  }
  const windowSet = new Set(historyKeys);
  const lastHistKey = historyKeys[historyKeys.length - 1] ?? null;

  // Trailing window: actual bars, with the forecast overlaid wherever it exists
  // (so the current month keeps its forecast point, not just its partial bar).
  const rows: TimelineRow[] = historyKeys.map((key) => {
    const quantity = histByKey.get(key) ?? 0;
    const attributed = attributedByKey.get(key) ?? 0;
    return {
      monthStart: key,
      quantity,
      orderCount: ordersByKey.get(key) ?? 0,
      forecast: forecastByKey.get(key),
      attributedQuantity: attributed,
      // Derived from the total rather than read straight off the point so the
      // two stacked bars always add up to the plotted total.
      nativeQuantity: quantity - attributed,
    };
  });

  // Forecast months past the trailing window (YYYY-MM-01 sorts chronologically).
  const forecastKeys = [...forecastByKey.keys()].sort();
  for (const key of forecastKeys) {
    if (lastHistKey === null || key > lastHistKey) {
      rows.push({ monthStart: key, forecast: forecastByKey.get(key) });
    }
  }

  // Divider sits at the last complete actual month before the forecast begins.
  let boundaryKey: string | null = null;
  const firstForecastKey = forecastKeys[0] ?? null;
  if (firstForecastKey) {
    const prev = prevMonthKey(firstForecastKey);
    if (windowSet.has(prev)) boundaryKey = prev;
  }

  return { rows, boundaryKey: forecast.length > 0 ? boundaryKey : null };
}

// Human label + swatch color per series, keyed by recharts dataKey. The single
// source of truth for a series' color, so the bar, its legend swatch and the
// tooltip can't drift apart.
const SERIES_META: Record<string, { label: string; color: string }> = {
  qty: { label: 'actual', color: '#3b82f6' },
  native: { label: 'own sales', color: '#3b82f6' },
  attributed: { label: 'attributed', color: '#06b6d4' },
  forecast: { label: 'forecast', color: '#f59e0b' },
  onHand: { label: 'on-hand', color: '#10b981' },
  orders: { label: 'sales', color: '#8b5cf6' },
};

// "sale" / "sales" as a standalone noun, so the count reads naturally in both
// the tooltip and the header totals. Two strings rather than one interpolated
// plural because translators need the singular and plural forms separately.
function salesNoun(gt: ReturnType<typeof useGT>, count: number): string {
  return count === 1
    ? gt('sale', { context: 'sales history chart, one sales order' })
    : gt('sales', { context: 'sales history chart, count of sales orders' });
}

// The order count rides on the chart row rather than on its own series, and
// recharts types that row as `any`. Read it back through a guard so a shape
// change downstream shows up as a missing count, not a runtime throw.
function readOrderCount(entry: unknown): number | null {
  if (typeof entry !== 'object' || entry === null) return null;
  const { payload } = entry as { payload?: unknown };
  if (typeof payload !== 'object' || payload === null) return null;
  const { orders } = payload as { orders?: unknown };
  return typeof orders === 'number' ? orders : null;
}

// Rebates-style tooltip: rounded card, soft border, colored swatch. Lists every
// series present at the hovered month (actual / forecast / on-hand). The actual
// bar also carries how many separate sales made up that month's units.
function ChartTooltip({
  active,
  payload,
  label,
}: Partial<TooltipContentProps<number, string>>) {
  const gt = useGT();
  if (!active || !payload || payload.length === 0) return null;
  const entries = payload.filter((e) => typeof e.value === 'number');
  if (entries.length === 0) return null;
  // When the sales line is plotted it gets its own row here, so the units row
  // must not repeat the count. Without the line, that inline suffix is the
  // only place the count appears.
  const hasOrdersSeries = entries.some((e) => e.dataKey === 'orders');
  return (
    <div className="rounded-lg border border-gray-200 bg-white/95 p-2 text-xs shadow-lg">
      <p className="mb-1 font-medium text-gray-800">{label}</p>
      {entries.map((entry) => {
        const key = typeof entry.dataKey === 'string' ? entry.dataKey : '';
        const meta = SERIES_META[key];
        const value = typeof entry.value === 'number' ? entry.value : 0;
        const swatch = (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: meta?.color ?? SERIES_META.qty.color }}
          />
        );
        // The sales line counts orders, not units — "8 sales" already names
        // itself, so it takes no trailing "(sales)" qualifier.
        if (key === 'orders') {
          return (
            <p key={key} className="flex items-center gap-2 text-gray-700">
              {swatch}
              <span>{`${value.toLocaleString()} ${salesNoun(gt, value)}`}</span>
            </p>
          );
        }
        // Only the actual bar has orders behind it — a forecast has none, and
        // neither does the attributed portion (those orders belong to the SKU
        // the rule drew them from). `native` is the actual bar once the history
        // is split into own-vs-attributed.
        const orders =
          (key === 'qty' || key === 'native') && !hasOrdersSeries
            ? readOrderCount(entry)
            : null;
        return (
          <p key={key} className="flex items-center gap-2 text-gray-700">
            {swatch}
            <span>
              {`${value.toLocaleString()} ${gt('units', {
                context: 'sales history chart, unit count suffix',
              })}`}
              {orders !== null
                ? ` · ${orders.toLocaleString()} ${salesNoun(gt, orders)}`
                : ''}
              {meta ? ` (${meta.label})` : ''}
            </span>
          </p>
        );
      })}
    </div>
  );
}

interface InnerChartProps {
  data: {
    month: string;
    qty?: number;
    native?: number;
    attributed?: number;
    orders?: number;
    onHand?: number;
    forecast?: number;
  }[];
  showOnHand: boolean;
  showForecast: boolean;
  // Split the history bar into own sales + quantity a copy/transfer rule
  // attributed here. Off when no month carries attributed demand, so the
  // unaffected 99% of products render exactly as before.
  showAttributed: boolean;
  // Plot the monthly sales-order count as a line on the right axis.
  showOrders: boolean;
  // X-axis label of the present month, drawn as a dashed boundary between
  // history and forecast. Null hides the marker.
  boundaryLabel: string | null;
  // Larger version renders thicker bars and bigger ticks.
  large?: boolean;
}

/**
 * Axis config for a series of whole numbers.
 *
 * recharts widens a small domain to fill its default five ticks, so a max of 1
 * became an axis running to 4 — which drew a full-height bar for "1 unit" next
 * to a quarter-height line for "1 sale" and made the two scales look unrelated.
 * Pinning the domain to the data and asking for only as many ticks as there are
 * whole steps keeps both series topping out together.
 */
function wholeNumberAxis(max: number): {
  domain: [number, number];
  tickCount: number;
  allowDecimals: false;
} {
  const top = Math.max(1, Math.ceil(max));
  return {
    domain: [0, top],
    tickCount: Math.min(5, top + 1),
    allowDecimals: false,
  };
}

// Every plotted quantity is a whole number. Sales are usually counted in
// eaches, but a weight- or length-priced item can move 12.5 of something, and
// forcing integer ticks there would round the axis out from under the bars.
function allWholeNumbers(values: (number | undefined)[]): boolean {
  return values.every((v) => v === undefined || Number.isInteger(v));
}

function InnerChart({
  data,
  showOnHand,
  showForecast,
  showAttributed,
  showOrders,
  boundaryLabel,
  large = false,
}: InnerChartProps) {
  // Derived here rather than passed down: InnerChart already has the rows, and
  // the axes are the only thing that needs these.
  const maxOrders = Math.max(0, ...data.map((d) => d.orders ?? 0));
  const unitsAreWhole =
    !showOnHand && allWholeNumbers(data.flatMap((d) => [d.qty, d.forecast]));
  const maxUnits = Math.max(
    0,
    ...data.map((d) => Math.max(d.qty ?? 0, d.forecast ?? 0)),
  );

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={data}
        margin={{
          top: large ? 16 : 4,
          right: large ? 16 : 4,
          bottom: large ? 16 : 4,
          left: large ? 8 : 0,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke="#e5e7eb"
        />
        <XAxis
          dataKey="month"
          tick={{ fontSize: large ? 12 : 10 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: large ? 12 : 10 }}
          axisLine={false}
          tickLine={false}
          width={large ? 44 : 32}
          {...(unitsAreWhole ? wholeNumberAxis(maxUnits) : {})}
        />
        {(showOnHand || showOrders) && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: large ? 12 : 10 }}
            axisLine={false}
            tickLine={false}
            width={large ? 44 : 32}
            {...(showOrders ? wholeNumberAxis(maxOrders) : {})}
          />
        )}
        <Tooltip cursor={{ fill: '#f3f4f6' }} content={<ChartTooltip />} />
        {showForecast && boundaryLabel && (
          <ReferenceLine
            yAxisId="left"
            x={boundaryLabel}
            stroke="#9ca3af"
            strokeDasharray="4 3"
          />
        )}
        {showAttributed ? (
          // Same total, two segments: the SKU's own sales at the bottom and the
          // share a copy/transfer rule attributed here on top, so the rule's
          // contribution reads off the bar without changing its height.
          <>
            <Bar
              yAxisId="left"
              dataKey="native"
              stackId="units"
              fill={SERIES_META.native.color}
            />
            <Bar
              yAxisId="left"
              dataKey="attributed"
              stackId="units"
              fill={SERIES_META.attributed.color}
              radius={[2, 2, 0, 0]}
            />
          </>
        ) : (
          <Bar
            yAxisId="left"
            dataKey="qty"
            fill={SERIES_META.qty.color}
            radius={[2, 2, 0, 0]}
          />
        )}
        {showForecast && (
          <Bar
            yAxisId="left"
            dataKey="forecast"
            fill={SERIES_META.forecast.color}
            fillOpacity={0.45}
            radius={[2, 2, 0, 0]}
          />
        )}
        {showOnHand && (
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="onHand"
            stroke={SERIES_META.onHand.color}
            strokeWidth={2}
            dot={{
              r: large ? 3 : 2,
              strokeWidth: 0,
              fill: SERIES_META.onHand.color,
            }}
            activeDot={{ r: large ? 5 : 4 }}
            isAnimationActive={false}
          />
        )}
        {showOrders && (
          // Forecast-only months carry no count, so the line stops at the
          // history boundary rather than being drawn across a gap it can't
          // speak to — `connectNulls` stays off deliberately.
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="orders"
            stroke={SERIES_META.orders.color}
            strokeWidth={2}
            dot={{
              r: large ? 3 : 2,
              strokeWidth: 0,
              fill: SERIES_META.orders.color,
            }}
            activeDot={{ r: large ? 5 : 4 }}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function SalesHistoryChart({
  salesHistory,
  onHandHistory,
  forecast,
  title,
  height = 128,
  monthsBack = 12,
}: SalesHistoryChartProps) {
  const gt = useGT();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { rows: timeline, boundaryKey } = useMemo(
    () => buildTimeline(salesHistory, monthsBack, forecast ?? []),
    [salesHistory, monthsBack, forecast],
  );

  const onHandByKey = useMemo(() => {
    const m = new Map<string, number>();
    if (onHandHistory)
      for (const p of onHandHistory) m.set(p.monthStart, p.onHand);
    return m;
  }, [onHandHistory]);

  const showOnHand = onHandHistory != null && onHandHistory.length > 0;
  const showForecast = forecast != null && forecast.length > 0;
  // Units and order counts live on different scales, so the sales line needs
  // the right axis. On-hand — an optional overlay no caller passes today —
  // owns that axis when present; two incompatible scales on one axis would
  // put both series in the wrong place, so the count falls back to the
  // tooltip rather than being drawn against someone else's numbers.
  const showOrders = !showOnHand && salesHistory.some((p) => p.orderCount > 0);
  // Only split the bar when a rule actually landed here. Every product without
  // a copy/transfer rule reports 0 attributed, and gets the single solid bar and
  // legend it has always had.
  const showAttributed = salesHistory.some(
    (p) => (p.attributedQuantity ?? 0) > 0,
  );
  const boundaryLabel = boundaryKey ? formatMonthShortLabel(boundaryKey) : null;

  const chartData = useMemo(
    () =>
      timeline.map((p) => ({
        month: formatMonthShortLabel(p.monthStart),
        qty: p.quantity,
        native: showAttributed ? p.nativeQuantity : undefined,
        attributed: showAttributed ? p.attributedQuantity : undefined,
        orders: showOrders ? p.orderCount : undefined,
        forecast: p.forecast,
        onHand: showOnHand ? (onHandByKey.get(p.monthStart) ?? 0) : undefined,
      })),
    [timeline, onHandByKey, showOnHand, showOrders, showAttributed],
  );

  const totalUnits = useMemo(
    () => salesHistory.reduce((sum, p) => sum + p.quantity, 0),
    [salesHistory],
  );

  // Sales, not units: the same 500 units read very differently as one order or
  // as fifty. Summed across months, so a customer who buys every month counts
  // once per month — these are orders over the window, not distinct customers.
  const totalOrders = salesHistory.reduce((sum, p) => sum + p.orderCount, 0);

  const totalsLabel = `${totalUnits.toLocaleString()} ${gt('units', {
    context: 'sales history chart, unit count suffix',
  })} · ${totalOrders.toLocaleString()} ${salesNoun(gt, totalOrders)}`;

  // Derive the fallback heading from `monthsBack` so callers passing
  // non-12 windows don't end up with a "last 12 months" label.
  const fallbackHeading = `${gt('Sales · last', {
    context: 'sales history default heading prefix',
  })} ${monthsBack} ${gt('months', {
    context: 'sales history default heading suffix',
  })}`;

  return (
    <div className="w-full">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-gray-500">
          {title ?? fallbackHeading}
        </span>
        <div className="flex items-center gap-2">
          {showAttributed && (
            <span className="flex items-center gap-1 text-xs text-cyan-600">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: SERIES_META.attributed.color }}
              />
              {gt('attributed', {
                context:
                  'sales chart legend label for demand a copy/transfer rule moved here from another product or warehouse',
              })}
            </span>
          )}
          {showForecast && (
            <span className="flex items-center gap-1 text-xs text-amber-600">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{
                  backgroundColor: SERIES_META.forecast.color,
                  opacity: 0.45,
                }}
              />
              {gt('forecast', { context: 'sales chart forecast legend label' })}
            </span>
          )}
          {showOrders && (
            <span className="flex items-center gap-1 text-xs text-violet-600">
              <span
                className="inline-block h-0.5 w-3 rounded-full"
                style={{ backgroundColor: SERIES_META.orders.color }}
              />
              {gt('sales', {
                context: 'sales chart order-count line legend label',
              })}
            </span>
          )}
          <span className="text-xs text-gray-500">{totalsLabel}</span>
          <button
            type="button"
            onClick={() => setIsFullscreen(true)}
            className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label={gt('Expand sales history chart', {
              context: 'aria-label for fullscreen sales history chart button',
            })}
            title={gt('Expand', {
              context: 'tooltip for fullscreen sales history chart button',
            })}
          >
            <ArrowsOutIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="w-full" style={{ height }}>
        <InnerChart
          data={chartData}
          showOnHand={showOnHand}
          showForecast={showForecast}
          showAttributed={showAttributed}
          showOrders={showOrders}
          boundaryLabel={boundaryLabel}
        />
      </div>

      {/* Same footprint as the suggestion-detail modal this chart usually sits
          in. At `max-w-5xl` with a fixed 60vh body the expanded chart came out
          taller than it was wide, which is the wrong shape for twelve months
          on an x-axis. */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="flex h-[90vh] w-[95vw] max-w-[1440px] flex-col sm:max-w-[1440px]">
          <DialogHeader>
            <DialogTitle>{title ?? fallbackHeading}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-gray-500">{totalsLabel}</div>
          <div className="min-h-0 w-full flex-1">
            <InnerChart
              data={chartData}
              showOnHand={showOnHand}
              showForecast={showForecast}
              showAttributed={showAttributed}
              showOrders={showOrders}
              boundaryLabel={boundaryLabel}
              large
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
